import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { adminApi, setToken, apiError } from "@/lib/adminApi";
import { Logo } from "@/components/common/Logo";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("credentials"); // credentials | twofa
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const attempt = async (withCode) => {
    setErr("");
    setLoading(true);
    try {
      const body = { email, password };
      if (withCode) body.code = code.trim();
      const { data } = await adminApi.post("/auth/login", body);
      if (data.requires_2fa) {
        setStep("twofa");
        return;
      }
      setToken(data.token);
      nav("/admin");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => { e.preventDefault(); attempt(step === "twofa"); };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FFFCFA] px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm rounded-[2rem] border border-[#F2E8E6] bg-white p-8 shadow-[0_24px_60px_-30px_rgba(213,142,120,0.5)]">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <Logo to="/" />
          <p className="text-xs uppercase tracking-[0.2em] text-[#8A7F86]">Admin Dashboard</p>
        </div>

        {step === "credentials" ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wider text-[#8A7F86]">Email</label>
              <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-3 text-sm text-[#2C2B2C] outline-none focus:border-[#FBC4A8] focus:ring-2 focus:ring-[#F4B999]/30" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wider text-[#8A7F86]">Password</label>
              <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-3 text-sm text-[#2C2B2C] outline-none focus:border-[#FBC4A8] focus:ring-2 focus:ring-[#F4B999]/30" />
            </div>
            {err && <p data-testid="admin-login-error" className="text-sm text-[#C0392B]">{err}</p>}
            <button data-testid="admin-login-submit" type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#232222] py-3.5 text-xs uppercase tracking-[0.18em] text-[#FFFCFA] transition-all hover:bg-[#4A2A38] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-3.5 w-3.5" />} Sign In
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="admin-2fa-step">
            <div className="flex flex-col items-center gap-2 text-center">
              <ShieldCheck className="h-8 w-8 text-[#FBC4A8]" />
              <p className="text-sm text-[#6E6168]">Enter the 6-digit code from your authenticator app (or a backup code).</p>
            </div>
            <input data-testid="admin-2fa-input" inputMode="numeric" autoFocus autoComplete="one-time-code" required value={code}
              onChange={(e) => setCode(e.target.value)} placeholder="123456"
              className="w-full rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-3 text-center text-lg tracking-[0.4em] text-[#2C2B2C] outline-none focus:border-[#FBC4A8] focus:ring-2 focus:ring-[#F4B999]/30" />
            {err && <p data-testid="admin-2fa-error" className="text-sm text-[#C0392B]">{err}</p>}
            <button data-testid="admin-2fa-submit" type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#232222] py-3.5 text-xs uppercase tracking-[0.18em] text-[#FFFCFA] transition-all hover:bg-[#4A2A38] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Verify &amp; Sign In
            </button>
            <button type="button" data-testid="admin-2fa-back" onClick={() => { setStep("credentials"); setCode(""); setErr(""); }}
              className="mx-auto flex items-center gap-1.5 text-xs text-[#8A7F86] hover:text-[#2C2B2C]">
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
